<template>
  <div class="healthcare-graphic">
    <div class="healthcare-graphic__top">
      <div class="verticalLineTop"></div>
      <sc-text v-if="fields && fields.headingCard" tag="h2" :field="fields.headingCard" />
      <div class="healthcare-graphic__card">
        <div class="horizontalLineCard"></div>
        <div class="verticalLineCard"></div>
        <div class="verticalLineCard2"></div>
        <div class="horizontalLineCardTopBot horizontalLineCardTop"></div>
        <div class="horizontalLineCardTopBot horizontalLineCardBottom"></div>
        <div class="cards">
          <sc-placeholder name="jss-healthcare-graphic-card" :rendering="rendering" />
        </div>
      </div>
    </div>
    <div class="healthcare-graphic__bottom">
      <div class="verticalLineBottom"></div>
      <sc-text v-if="fields && fields.headingIcon" tag="h2" :field="fields.headingIcon" />
      <div class="healthcare-graphic__data">
        <sc-placeholder name="jss-healthcare-graphic-data" :rendering="rendering" />
      </div>
      <ol class="healthcare-footnote">
        <sc-placeholder name="jss-healthcare-graphic-footnote" :rendering="rendering" />
      </ol>
    </div>
  </div>
</template>

<script>
import { Text, Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import { onMounted, ref, watch } from 'vue';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'CIS-Healthcare-Graphic',
  components: {
    ScText: Text,
    ScPlaceholder: Placeholder
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    rendering: {
      type: Object
    }
  },
  setup() {
    const screenSize = ref('');
    const desktopTimeline = ref(null);

    onMounted(() => {
      if (window.innerWidth >= 768) screenSize.value = 'large';
      else screenSize.value = 'small';

      const handleResize = () => {
        if (window.innerWidth >= 768) screenSize.value = 'large';
        else screenSize.value = 'small';
      };

      if (window) {
        window.addEventListener('resize', handleResize);
      }
    });

    // Watcher
    watch(screenSize, (newValue) => {
      if (newValue === 'large') {
        desktopTimeline.value = gsap
          .timeline({
            paused: true,
            scrollTrigger: '.healthcare-graphic__top'
          })
          .set('.verticalLineTop', { height: 0 })
          .set('.mobileHorizontalLineCard', { width: 0 })
          .set('.card__container', { opacity: 0 })
          .set('.cardIcon', { opacity: 0, top: '3rem' })
          .set('.horizontalLineData', { width: 0 })
          .set('.verticalLineBottom', { height: 0 })
          .set('.data__icon', { opacity: 0, top: '50%' })
          .to('.verticalLineTop', {
            height: '100%',
            duration: 0.75,
            ease: 'none',
            onComplete: function () {
              gsap
                .timeline({ scrollTrigger: '.healthcare-graphic__bottom' })
                .to('.verticalLineBottom', {
                  height: '100%',
                  duration: 2,
                  delay: 0.5,
                  ease: 'none'
                })
                .to(
                  '.horizontalLineData',
                  {
                    duration: 0.25,
                    width: '100%',
                    ease: 'none',
                    delay: 1,
                    stagger: {
                      each: 0.25
                    }
                  },
                  '<'
                )
                .to(
                  '.data__icon',
                  {
                    duration: 0.5,
                    top: 0,
                    opacity: 1,
                    ease: 'none',
                    stagger: {
                      each: 0.25
                    }
                  },
                  '<'
                );
            }
          })
          .to(
            '.horizontalLineCard',
            {
              width: '3rem',
              duration: 0.25,
              delay: 0.4,
              ease: 'none'
            },
            '<'
          )
          .to(
            ['.verticalLineCard', '.verticalLineCard2'],
            {
              height: '38%',
              duration: 0.25,
              delay: 0.25,
              ease: 'none'
            },
            '<'
          )
          .to('.horizontalLineCardTopBot', {
            width: '2.5rem',
            duration: 0.25,
            ease: 'none'
          })
          .to(
            '.card__container',
            {
              duration: 0.75,
              opacity: 1,
              delay: 0.25,
              stagger: {
                each: 0.5
              }
            },
            '<'
          )
          .to(
            '.cardIcon',
            {
              duration: 0.75,
              opacity: 1,
              top: 0,
              delay: 0.5,
              stagger: {
                each: 0.5
              }
            },
            '<'
          );
      } else {
        desktopTimeline.value = gsap
          .timeline({
            paused: true,
            scrollTrigger: '.healthcare-graphic__top'
          })
          .set('.verticalLineTop', { height: 0 })
          .set('.horizontalLineCard', { width: 0 })
          .set(['.verticalLineCard', '.verticalLineCard2'], { height: 0 })
          .set('.horizontalLineCardTopBot', { width: 0 })
          .set('.card__container', { opacity: 0 })
          .set('.cardIcon', { opacity: 0, top: '3rem' })
          .set('.verticalLineBottom', { height: 0 })
          .set('.horizontalLineData', { width: 0 })
          .set('.data__icon', { opacity: 0, top: '50%' })
          .to('.verticalLineTop', {
            height: '100%',
            duration: 3,
            ease: 'none'
          })
          .to(
            '.mobileHorizontalLineCard',
            {
              width: '2rem',
              duration: 0.75,
              delay: 0.5,
              ease: 'none',
              stagger: {
                each: 1.25
              },
              onComplete: function () {
                gsap
                  .timeline({ scrollTrigger: '.healthcare-graphic__bottom' })
                  .to('.verticalLineBottom', {
                    height: '100%',
                    duration: 4,
                    delay: 0.2,
                    ease: 'none'
                  })
                  .to(
                    '.horizontalLineData',
                    {
                      duration: 0.75,
                      width: '5rem',
                      delay: 1.2,
                      ease: 'none',
                      stagger: {
                        each: 0.5
                      }
                    },
                    '<'
                  )
                  .to(
                    '.data__icon',
                    {
                      duration: 0.75,
                      top: 0,
                      opacity: 1,
                      stagger: {
                        each: 0.5
                      }
                    },
                    '<'
                  );
              }
            },
            '<'
          )
          .to(
            '.card__container',
            {
              duration: 0.75,
              opacity: 1,
              delay: 0.5,
              stagger: {
                each: 1.25
              }
            },
            '<'
          )
          .to(
            '.cardIcon',
            {
              duration: 0.75,
              opacity: 1,
              top: 0,
              stagger: {
                each: 1.25
              }
            },
            '<'
          );
      }
    });

    return {
      screenSize,
      desktopTimeline
    };
  }
};
</script>
<style lang="scss" scoped>
.healthcare-graphic {
  position: relative;
  margin: 0 2rem;

  @include tablet-up {
    margin-bottom: 3rem;
  }

  &__top {
    position: relative;
  }

  &__bottom {
    position: relative;
    padding-top: 3rem;
  }

  &__card {
    position: relative;

    .cards {
      @include tablet-up {
        display: flex;
        margin-left: 6rem;
      }
    }
  }

  &__data {
    position: relative;
    padding-bottom: 3rem;

    @include tablet-up {
      display: flex;
    }
  }

  h2 {
    margin-left: 2rem;
  }

  .verticalLineTop,
  .verticalLineBottom {
    position: absolute;
    top: 0;
    left: 0;
    height: 0;
    width: 0.1rem;
    background-color: $divider;
  }

  .verticalLineCard {
    position: absolute;
    top: calc(50%);
    left: 3rem;
    width: 0.1rem;
    background-color: $divider;

    @include phone-only {
      display: none;
    }
  }

  .verticalLineCard2 {
    position: absolute;
    top: calc(50%);
    left: 3rem;
    height: 0;
    width: 0.1rem;
    background-color: $divider;
    transform: translateY(-100%) rotate(-180deg);

    @include phone-only {
      display: none;
    }
  }

  .horizontalLineCard {
    position: absolute;
    top: 50%;
    left: 0;
    height: 0.1rem;
    width: 0;
    background-color: $divider;
  }

  .horizontalLineCardTop {
    position: absolute;
    top: 12%;
    left: 3rem;
    height: 0.1rem;
    width: 0;
    background-color: $divider;
  }

  .horizontalLineCardBottom {
    position: absolute;
    bottom: 12%;
    left: 3rem;
    height: 0.1rem;
    width: 0;
    background-color: $divider;
  }

  .healthcare-footnote {
    padding-top: 3rem;
  }

  .healthcare-graphic__data {
    &:deep() {
      > div:nth-child(1) {
        @include tablet-up {
          padding-left: 3rem;
        }
      }
    }
  }
}
</style>
